import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { LUX_PLUS_MONTH_HIGHLIGHT_BLUE, LUX_PLUS_MONTH_TAKEOVER_END_DATE, LUX_PLUS_MONTH_TAKEOVER_START_DATE } from '../constants/luxPlusMonthTakeoverConstants'
import LuxPlusMonthCalendarLogo from './LuxPlusMonthCalendarLogo'

const Hero = styled(Sheet)`
  background:
    url(${getImageUrl('lux-plus-month-plus-texture-desktop')}) no-repeat bottom center / 100%,
    radial-gradient(100% 101.45% at 0% 1.74%, #4866DE 0%, #4054CB 16.37%, #3645BE 24.83%, #2A36B4 33.74%, #202CAE 41.88%, #1923A7 47.59%, #1B1E9B 55%, #202199 61.87%, #171891 69.31%, #12158A 79.48%, #0F1476 89.22%, #0C156E 100%);
`

const HeroContent = styled(Group)`
  min-height: ${rem(108)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(176)};
  }
`

const Subheading = styled(Heading)`
  color: ${LUX_PLUS_MONTH_HIGHLIGHT_BLUE};
`

function LuxPlusMonthInfoModal() {
  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const startFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (loginResult.loggedIn) {
        dispatch(createFreePreviewMember())
        modalElement.resolve()
      }
    } else {
      dispatch(createFreePreviewMember())
      modalElement.resolve()
    }
  }, [dispatch, isLoggedIn, showModal, modalElement])

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const intl = useIntl()
  const startFreePreviewLabel = intl.formatMessage({
    id: 'khLwmQpxy2drsVVxEMUb',
    defaultMessage: 'Start your FREE preview',
    description: 'Button text to start free LuxPlus+ preview in LuxPlus+ Month info modal',
  })

  return <ModalBase size="S">
    <ModalHeader title="" isOverlay paletteType="inverse" />
    <ModalBody>
      <Hero padding="40 24" paletteType="inverse">
        <HeroContent direction="vertical" gap={20} verticalAlign="end">
          <LuxPlusMonthCalendarLogo size="M" />
          <Group direction="vertical" gap={12}>
            <Heading variant="heading1">
              <FormattedMessage
                id="y8K1k6geFhZzjrbb-TnF"
                defaultMessage="LuxPlus<plus>+</plus> Month"
                description="Brand heading for LuxPlus+ Month campaign in info modal"
                values={{
                  plus: (chunks) => <Colour value="highlight-lux-plus">{chunks}</Colour>,
                }}
              />
            </Heading>
            <Subheading variant="heading3"><FormatDateTimeRange startValue={LUX_PLUS_MONTH_TAKEOVER_START_DATE} endValue={LUX_PLUS_MONTH_TAKEOVER_END_DATE} day="numeric" month="long" /></Subheading>
          </Group>
        </HeroContent>
      </Hero>
      <ModalContent>
        {!canViewLuxPlusBenefits && <Group direction="vertical" gap={16}>
          <BodyText variant="large" weight="bold">
            {intl.formatMessage({
              id: 'k_8YBIGrFS7WfuB1LL1r',
              defaultMessage: 'Four weeks of elevated perks for our VIP travel club.',
              description: 'Bold body text describing LuxPlus+ Month perks in info modal',
            })}
          </BodyText>
          <BodyText variant="large">
            <FormattedMessage
              id="axL9BV4-9dvf-UgDU_Ze"
              defaultMessage="For the month of {month} unlock bigger savings, more hidden escapes and more exclusive early-access offers - <bold>enjoy a {duration}-day FREE preview and browse like a VIP.</bold>"
              description="Body text describing LuxPlus+ Month savings and free preview offer in info modal"
              values={{
                month: <FormatDateTime value={LUX_PLUS_MONTH_TAKEOVER_START_DATE} month="long" />,
                duration: LUXURY_PLUS.FREE_PREVIEW_DURATION,
                bold: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
          <BodyText variant="medium" weight="bold">
            <FormattedMessage
              id="nob0B7WrenZSyUab0iiE"
              defaultMessage="No credit card needed. <link>T&Cs apply</link>"
              description="No credit card notice with terms and conditions link in LuxPlus+ Month info modal"
              values={{
                link: (chunks) => <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} target="_blank">{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </Group>}
        {canViewLuxPlusBenefits && <Group direction="vertical" gap={16}>
          <BodyText variant="large">
            <FormattedMessage
              id="X1X2KVrjWDTHTq5TiB_v"
              defaultMessage="Introducing <bold>{campaignName}</bold> - four weeks of elevated perks just for members."
              description="Introduction text for LuxPlus+ Month campaign for existing members in info modal"
              values={{
                campaignName: 'LuxPlus+ Month',
                bold: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
          <BodyText variant="large">
            <FormattedMessage
              id="NlLqG78JG9GYlqzmLBwl"
              defaultMessage="Enjoy bigger savings, more hidden escapes, and more early-access offers throughout {month}."
              description="Body text about LuxPlus+ Month benefits for existing members in info modal"
              values={{
                month: <FormatDateTime value={LUX_PLUS_MONTH_TAKEOVER_START_DATE} month="long" />,
              }}
            />
          </BodyText>
          <BodyText variant="large">
            {intl.formatMessage({
              id: 'oNaa-7yi24KZvc1FCGry',
              defaultMessage: 'This is the your VIP treatment, upgraded.',
              description: 'Closing statement about VIP treatment in LuxPlus+ Month info modal',
            })}
          </BodyText>
        </Group>}
      </ModalContent>
    </ModalBody>
    {!canViewLuxPlusBenefits && <ModalFooter primaryActionProps={{ children: startFreePreviewLabel, onClick: startFreePreview }} />}
  </ModalBase>
}

export default LuxPlusMonthInfoModal
